import React from "react";
import { Button, Space, Input, Tooltip, Select, Checkbox, Tag } from "antd";
import {
	EditOutlined,
	CheckOutlined,
	CloseOutlined,
	ArrowRightOutlined,
	ArrowLeftOutlined,
	InfoCircleOutlined,
	HistoryOutlined,
} from "@ant-design/icons";
import { SquarePen } from "lucide-react";
import { ROLES, STAGES } from "components/common/Constants";
import { getFormalName, getNextStage, getPreviousStage } from "components/services";
import {
	PAYMENT_STATUS_OPTIONS,
	getStatusColor,
	formatStatus,
	PRIORITY_HOVER_TEXT_COLOR,
} from "./cardsTableUtils";
import { ButtonContainer, StyledButton } from "./CardsTableStyles";

// Base columns that are always shown
export const getBaseColumns = ({ isCompleted, navigate, user }) => {
	const columns = [
		{
			title: "#",
			dataIndex: "serial_number",
			key: "serial_number",
			render: (serial_number, record) => serial_number || record.id || "—",
		},
		{
			title: "Client Company",
			dataIndex: "client_details",
			key: "client",
			render: (clientDetails, record) => {
				const normalColor = isCompleted
					? "#880088ff"
					: record.priority === 3 || record.priority === 4
						? "#FFFFFF"
						: "#880088ff";
				const hoverColor = isCompleted
					? "#000000"
					: PRIORITY_HOVER_TEXT_COLOR[record.priority] || "#000000";

				const companyName = clientDetails?.company_name || "N/A";
				const assignedUsers = clientDetails?.assigned_users_detail || [];

				return (
					<Tooltip title={`Click here to open the jobcard details`}>
						<a
							onClick={() => navigate(`/detail/${record.id}`)}
							className="client-link"
							style={{
								color: normalColor,
								textDecoration: "none",
								cursor: "pointer"
							}}
							onMouseEnter={(e) => (e.currentTarget.style.color = hoverColor)}
							onMouseLeave={(e) => (e.currentTarget.style.color = normalColor)}
						>
							{companyName}
						</a>
						{assignedUsers.length > 0 && (
							<div style={{ marginTop: "4px" }}>
								{assignedUsers.map((user, index) => {
									const displayName =
										user.first_name?.trim() && user.last_name?.trim()
											? `${user.first_name} ${user.last_name}`
											: user.first_name?.trim() || user.last_name?.trim() || user.username;

									return (
										<Tag key={index} color="blue" variant="solid" style={{ fontSize: "10px", backgroundColor: "#ffffff", color: "blue", marginBottom: "1px" }}>
											{displayName}
										</Tag>
									);
								})}
							</div>
						)}
					</Tooltip >
				);
			},
		},
		{
			title: "Date",
			dataIndex: "date",
			key: "date",
		},
		{
			title: "Del. Date",
			dataIndex: "deliver_date",
			key: "deliver_date",
		},
		{
			title: "Created By",
			dataIndex: "Created_by",
			key: "Created_by",
		},
		{
			title: "Last Updated",
			dataIndex: "last_update",
			key: "last_update",
		},
		{
			title: "Qty",
			dataIndex: "quantity",
			key: "quantity",
		},
	];

	if (user && [ROLES.DESIGN, ROLES.ADMIN, ROLES.PACKING, ROLES.ACCOUNT].includes(user.roll)) {
		columns.push({
			title: "FOC Qty",
			dataIndex: "foc_qty",
			key: "foc_qty",
		});
	}

	columns.push({
		title: "A/S",
		dataIndex: "notes",
		key: "techniques",
		render: (notes) => {
			const techniqueDisplay = getTechniquesFromNotes(notes);

			if (!techniqueDisplay) return "-";

			// Optional: Add tooltip to show full technique names
			const tooltipText = techniqueDisplay === "A & S"
				? "Airhole & Sublimation"
				: techniqueDisplay === "A"
					? "Airhole"
					: "Sublimation";

			return (
				<Tooltip title={tooltipText}>
					<Tag style={{ backgroundColor: "white", border: "none", borderRadius: "5px" }} color={techniqueDisplay === "A & S" ? "purple" : techniqueDisplay === "A" ? "blue" : "green"}>
						{techniqueDisplay}
					</Tag>
				</Tooltip>
			);
		},
	});

	return columns;
};

// Rate column for viewer role
export const getRateColumn = () => ({
	title: "Rate",
	dataIndex: "rate",
	key: "rate",
	render: (value) => (value || value === 0 ? value : "N/A"),
});
export const getTechniquesFromNotes = (notes) => {
	if (!notes) return "";

	try {
		let notesObj = notes;
		if (typeof notes === "string") {
			const trimmed = notes.trim();
			if (!trimmed.startsWith("{") && !trimmed.startsWith("[")) {
				return "";
			}
			notesObj = JSON.parse(trimmed);
		}

		const techniqueMap = notesObj?.technique_map || {};
		if (!techniqueMap || typeof techniqueMap !== "object") return "";

		// Get unique techniques (excluding null values)
		const techniques = new Set();
		Object.values(techniqueMap).forEach(technique => {
			if (technique) {
				techniques.add(technique);
			}
		});

		// Convert to array and check for AIRHOLE and SUBLIMATION
		const techniquesArray = Array.from(techniques);
		const hasAirhole = techniquesArray.includes("AIRHOLE");
		const hasSublimation = techniquesArray.includes("SUBLIMATION");

		if (hasAirhole && hasSublimation) {
			return "A & S";
		} else if (hasAirhole) {
			return "A";
		} else if (hasSublimation) {
			return "S";
		}

		return "";
	} catch (error) {
		return "";
	}
};
// Current stage / Emb placement column
export const getStageColumn = ({ isCompleted }) => ({
	title: isCompleted ? "Emb Placement" : "Current Stage",
	dataIndex: isCompleted ? "embr_Front" : "current_stage",
	key: isCompleted ? "emb_placement" : "current_stage",
	render: (value, record) => {
		if (isCompleted) {
			const placements = [
				record.embr_Front,
				record.embr_Back,
				record.embr_Left_Middle,
				record.embr_Right_Middle,
				record.embr_Visor,
				record.embr_Velcro,
				record.embr_Visor_Square,
				record.embr_One_Side_Emb,
			].filter(Boolean);
			return placements.length > 0 ? placements.join(", ") : "—";
		}
		if (record._isRejected === true) {
			return "Revision";
		}
		const originalStage = record._originalCurrentStage;
		const isNullOriginalStage =
			!originalStage || originalStage === null || originalStage === "" || originalStage === undefined;
		const isDigitizerStage =
			record.is_degitizer_stage === true ||
			record.is_degitizer_stage === "true" ||
			record.is_degitizer_stage === 1;

		if (isNullOriginalStage && isDigitizerStage) {
			return "Revision";
		}
		return getFormalName(record.current_stage);
	},
});

// Priority number readonly column (for non-admin/design roles)
export const getPriorityReadonlyColumn = () => ({
	title: "Priority Number",
	key: "priority_number_readonly",
	render: (_, record) => (
		<span>{record.priority_number != null ? record.priority_number : "Not Set"}</span>
	),
});

// Payment status column
export const getPaymentStatusColumn = ({ user, updateJobcard }) => ({
	title: "Payment Status",
	dataIndex: "status",
	key: "status",
	render: (text, record) => {
		const status = record.status || text || "pending";
		const formattedStatus = formatStatus(status);
		const statusColors = getStatusColor(status);

		// Container style to isolate from row colors
		const containerStyle = {
			display: "inline-block",
			padding: "2px",
			borderRadius: "6px",
			backgroundColor: "#ffffff",
		};

		if (user.roll === ROLES.PAYMENT) {
			return (
				<div style={containerStyle}>
					<Select
						value={status}
						style={{
							width: "100%",
							minWidth: "130px",
						}}
						onChange={async (newStatus) => {
							try {
								await updateJobcard({
									id: record.id,
									status: newStatus,
								}).unwrap();
							} catch (error) {
								// error handled in parent
							}
						}}
						options={PAYMENT_STATUS_OPTIONS.map((option) => {
							const optionColors = getStatusColor(option.value);
							return {
								...option,
								label: (
									<span
										style={{
											fontWeight: 500,
											color: "#000000",
											padding: "2px 6px",
											borderRadius: "4px",
											backgroundColor: "transparent",
											border: `1px solid ${optionColors.border}`,
											display: "inline-block",
										}}
									>
										{option.label}
									</span>
								),
							};
						})}
						dropdownStyle={{
							zIndex: 1050,
							backgroundColor: "#ffffff",
						}}
						onClick={(e) => e.stopPropagation()}
						labelRender={({ label, value }) => {
							const selectedColors = getStatusColor(value);
							return (
								<span
									style={{
										fontWeight: 500,
										color: "#000000",
										padding: "2px 8px",
										borderRadius: "4px",
										backgroundColor: selectedColors.background,
										border: `1px solid ${selectedColors.border}`,
										display: "inline-block",
									}}
								>
									{formatStatus(value)}
								</span>
							);
						}}
					/>
				</div>
			);
		}

		return (
			<div style={containerStyle}>
				<span
					style={{
						padding: "4px 8px",
						borderRadius: "4px",
						border: `1px solid ${statusColors.border}`,
						backgroundColor: statusColors.background,
						color: "#000000",
						fontWeight: 500,
						display: "inline-block",
					}}
				>
					{formattedStatus}
				</span>
			</div>
		);
	},
});

// Priority number editable column (for admin/design roles)
export const getPriorityEditableColumn = ({
	editingPriorityId,
	editingPriorityValue,
	setEditingPriorityId,
	setEditingPriorityValue,
	dispatch,
	UpdateJobCardPriority,
	user,
}) => ({
	title: "Priority Number",
	key: "priority_number",
	render: (_, record) => {
		if (record.current_stage === "completed") return null;

		return (
			<Space>
				{editingPriorityId === record.id ? (
					<>
						<Input
							type="number"
							min={1}
							value={editingPriorityValue}
							style={{ width: "80px" }}
							onClick={(e) => e.stopPropagation()}
							onChange={(e) =>
								setEditingPriorityValue(e.target.value ? parseInt(e.target.value) : null)
							}
						/>
						<Button
							type="text"
							className="success-button"
							icon={<CheckOutlined />}
							onClick={(e) => {
								e.stopPropagation();
								dispatch(
									UpdateJobCardPriority({
										id: record.id,
										priority_number: editingPriorityValue,
										priority: record.priority,
									})
								);
								setEditingPriorityId(null);
								setEditingPriorityValue(null);
							}}
						/>
						<Button
							type="text"
							icon={<CloseOutlined />}
							className="danger-button"
							onClick={(e) => {
								e.stopPropagation();
								setEditingPriorityId(null);
								setEditingPriorityValue(null);
							}}
						/>
					</>
				) : (
					<>
						<span>{record.priority_number != null ? record.priority_number : "Not Set"}</span>
						{[ROLES.ADMIN, ROLES.DESIGN].includes(user.roll) && (
							<Button
								type="text"
								icon={<EditOutlined />}
								className="edit-button"
								onClick={(e) => {
									e.stopPropagation();
									setEditingPriorityId(record.id);
									setEditingPriorityValue(
										record.priority_number != null ? record.priority_number : ""
									);
								}}
							/>
						)}
					</>
				)}
			</Space>
		);
	},
});

// Actions column (update + activity log)
export const getActionsColumn = ({ navigate, setActivityLogModal }) => ({
	title: "Actions",
	key: "update",
	align: "center",
	render: (_, record) => (
		<Space size="middle">
			<Tooltip title="Update Jobcard">
				<span
					onClick={(e) => {
						e.stopPropagation();
						navigate(`update/${record.id}`);
					}}
					role="button"
					aria-label="Edit jobcard"
					style={{
						display: "inline-flex",
						alignItems: "center",
						cursor: record.current_stage === STAGES.COMPLETED ? "not-allowed" : "pointer",
						opacity: record.current_stage === STAGES.COMPLETED ? 0.5 : 1,
					}}
				>
					<SquarePen size={18} color="#22c55e" />
				</span>
			</Tooltip>
			<Tooltip title="View Activity Log">
				<HistoryOutlined
					onClick={(e) => {
						e.stopPropagation();
						setActivityLogModal({ visible: true, jobcardId: record.id });
					}}
					style={{
						fontSize: 18,
						color: "#1890ff",
						cursor: "pointer",
					}}
				/>
			</Tooltip>
		</Space>
	),
});

// Review column (admin only - send back)
export const getReviewColumn = ({ updateJobcard }) => ({
	title: "Review",
	key: "review",
	align: "center",
	render: (_, record) => (
		<ButtonContainer>
			<StyledButton
				icon={<ArrowLeftOutlined />}
				size="large"
				onClick={(e) => {
					e.stopPropagation();
					const prevStage = getPreviousStage(record.current_stage);
					const payload = {
						id: record.id,
						current_stage: prevStage,
					};
					if (record.current_stage === STAGES.CUTTING && prevStage === STAGES.CREATION) {
						payload.is_degitizer_stage = false;
					}
					updateJobcard(payload);
				}}
				disabled={
					record.current_stage === STAGES.CREATION || record.current_stage === STAGES.COMPLETED
				}
			>
				{record.current_stage === STAGES.COMPLETED
					? "Completed"
					: `Send back to ${getFormalName(getPreviousStage(record.current_stage))}`}
			</StyledButton>
		</ButtonContainer>
	),
});

// Info column for packing/billing/dispatch (in-progress)
export const getInfoColumn = ({ setInfoModal }) => ({
	title: "Info",
	key: "info",
	align: "center",
	render: (_, record) => (
		<Tooltip title="View jobcard info">
			<span
				onClick={(e) => {
					e.stopPropagation();
					setInfoModal({ visible: true, jobcardId: record.id });
				}}
				role="button"
				aria-label="View jobcard info"
				style={{
					display: "inline-flex",
					alignItems: "center",
					cursor: "pointer",
					color: "#1890ff",
				}}
			>
				<InfoCircleOutlined />
			</span>
		</Tooltip>
	),
});

// Completed info column (dispatch summary)
export const getCompletedInfoColumn = ({ setCompletedInfoModal }) => ({
	title: "Info",
	key: "completed_info",
	align: "center",
	render: (_, record) => (
		<Tooltip title="View dispatch summary">
			<span
				role="button"
				aria-label="View dispatch summary"
				onClick={(e) => {
					e.stopPropagation();
					setCompletedInfoModal({
						visible: true,
						jobcardId: record.id,
					});
				}}
				style={{
					display: "inline-flex",
					alignItems: "center",
					justifyContent: "center",
					color: "#1890ff",
					cursor: "pointer",
				}}
			>
				<InfoCircleOutlined />
			</span>
		</Tooltip>
	),
});

// Dispatch checkbox column
export const getDispatchCheckboxColumn = ({
	safeSelectedRowKeys,
	handleDispatchCheckboxChange,
}) => ({
	title: "",
	key: "dispatch_select",
	width: 50,
	align: "center",
	render: (_, record) => (
		<Checkbox
			checked={safeSelectedRowKeys.includes(record.id)}
			onChange={(e) => {
				e.stopPropagation();
				handleDispatchCheckboxChange(record, e.target.checked);
			}}
		/>
	),
});

// Select column for CUTTING and ADMIN roles
export const getSelectColumn = ({
	safeSelectedRowKeys,
	recordBig,
	setSelectedRowKeys,
	setRecordBig,
}) => ({
	title: "Gen Qty",
	key: "select",
	width: 50,
	align: "center",
	render: (_, record) => (
		<Checkbox
			checked={safeSelectedRowKeys.includes(record?.id)}
			onChange={(e) => {
				e.stopPropagation();
				const checked = e.target.checked;
				if (checked) {
					const newKeys = [...safeSelectedRowKeys, record?.id];
					const newRecords = [...(Array.isArray(recordBig) ? recordBig : []), record];
					setSelectedRowKeys?.(newKeys);
					setRecordBig?.(newRecords);
				} else {
					const newKeys = safeSelectedRowKeys.filter((id) => id !== record?.id);
					const newRecords = (Array.isArray(recordBig) ? recordBig : []).filter(
						(item) => item?.id !== record?.id
					);
					setSelectedRowKeys?.(newKeys);
					setRecordBig?.(newRecords);
				}
			}}
		/>
	),
});
